// "Haftalık Raporlar" — the weekly per-agent overall report (owner 2026-09-29, Task Board
// #145 Vegas / #146 Prime).
//
// A QMS specialist writes one free-text report per agent of their brand for each Mon–Sun
// week, due the Tuesday after at 10:30 Istanbul. The same screen is the supervisor's and the
// admin's reading room: the text is visible only to admin, the brand's report-reader
// supervisor (bulut → Vegas, bora → Prime) and the author — the server sends `body: null` to
// anyone else, so this screen never has to decide who may read what.
//
// The agent's week (count · average · criticals) is shown next to each row as read-only
// context, so the specialist writes the report with the numbers in front of them.
//
// WeeklyReportBanner is the reminder: a qms sees it on every other screen while any report of
// the DUE week (the last completed one) is still missing — amber before the deadline, red
// after it.

import { useEffect, useState } from 'react';
import * as api from '../api';
import { ApiError } from '../api';
import type { PanelUser, WeeklyReportAgent, WeeklyReportBoard } from '../types';
import { useAsync } from '../hooks';
import { Avatar, Callout, Empty, Icon, Loading, Note, PageHead, Panel, Pill, ScorePill } from '../ui';
import { fullDateTime } from '../format';
import { navigate } from '../App';

export default function WeeklyReports({ user, brand }: {
  user: PanelUser;
  /** The topbar's global brand filter; '' = every brand this user's board covers. */
  brand: string;
}) {
  // '' = the due week (the server resolves it), so the screen opens on what is owed.
  const [week, setWeek] = useState('');
  const state = useAsync(() => api.getWeeklyReports(week || undefined, brand), [week, brand]);
  const board = state.data;
  const isQms = user.role === 'qms';

  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // A different week/brand closes any open editor — its draft belongs to another row.
  useEffect(() => { setEditing(null); setError(''); }, [week, brand]);

  const startEdit = (a: WeeklyReportAgent) => {
    setEditing(a.agentId);
    setDraft(a.report?.body ?? '');
    setError('');
  };

  const save = async (agentId: number) => {
    if (!board || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.saveWeeklyReport(agentId, board.week.id, draft);
      setEditing(null);
      state.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rapor kaydedilemedi.');
    } finally {
      setBusy(false);
    }
  };

  const isDueWeek = board ? board.week.id === board.dueWeekId : false;
  const missing = board ? board.brands.reduce((s, b) => s + (b.total - b.submitted), 0) : 0;

  return (
    <>
      <PageHead
        icon="speaker_notes"
        title="Haftalık Raporlar"
        subtitle={isQms
          ? 'Markandaki her temsilci için haftalık genel rapor. Son teslim: haftanın ardından gelen Salı 10:30.'
          : 'QMS uzmanlarının temsilci bazında haftalık genel raporları.'}
        right={board ? (
          <select
            className="inp auto" value={board.week.id}
            onChange={(ev) => setWeek(ev.target.value)}
            aria-label="Hafta"
          >
            {board.weeks.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
          </select>
        ) : null}
      />

      {board ? (
        missing === 0 && board.brands.length > 0 ? (
          <Callout tone="good">Bu haftanın bütün raporları gönderildi.</Callout>
        ) : board.overdue ? (
          <Callout tone="bad">
            {`Son teslim geçti (${fullDateTime(board.deadline)}) · ${missing} rapor eksik`}
          </Callout>
        ) : (
          <Callout tone={isDueWeek ? 'warn' : 'neu'}>
            {`Son teslim: ${fullDateTime(board.deadline)} · ${missing} rapor eksik`}
          </Callout>
        )
      ) : null}

      {state.error ? <Note error>{state.error}</Note> : null}
      {state.loading && !board ? <Loading /> : null}
      {board && board.brands.length === 0 ? (
        <Empty>Bu seçimde görebileceğiniz haftalık rapor yok — raporları yalnız markanın süpervizörü ve yönetici görür.</Empty>
      ) : null}

      {board?.brands.map((b) => (
        <Panel
          key={b.key}
          icon="groups"
          title={b.name}
          hint={`${b.submitted}/${b.total} gönderildi`}
        >
          {b.agents.length === 0 ? <Empty>Bu markada aktif temsilci yok.</Empty> : null}
          {b.agents.map((a) => (
            <AgentRow
              key={a.agentId}
              a={a}
              board={board}
              canWrite={board.canWrite}
              open={editing === a.agentId}
              draft={draft}
              busy={busy}
              error={editing === a.agentId ? error : ''}
              onDraft={setDraft}
              onEdit={() => startEdit(a)}
              onCancel={() => setEditing(null)}
              onSave={() => save(a.agentId)}
            />
          ))}
        </Panel>
      ))}
    </>
  );
}

function AgentRow({ a, board, canWrite, open, draft, busy, error, onDraft, onEdit, onCancel, onSave }: {
  a: WeeklyReportAgent; board: WeeklyReportBoard; canWrite: boolean;
  open: boolean; draft: string; busy: boolean; error: string;
  onDraft: (s: string) => void; onEdit: () => void; onCancel: () => void; onSave: () => void;
}) {
  const r = a.report;
  const status = r
    ? (r.late ? <Pill tone="warn">Geç gönderildi</Pill> : <Pill tone="good">Gönderildi</Pill>)
    : <Pill tone={board.overdue ? 'bad' : 'warn'}>Eksik</Pill>;
  // A missing report can be written by a qms (late if past the deadline); a submitted one
  // only by its author before the deadline (server: canEditWeeklyReport).
  const action = open ? null
    : !r && canWrite ? 'Rapor yaz'
      : r?.canEdit ? 'Düzenle'
        : null;

  return (
    <div className="aprow" style={{ cursor: 'default' }}>
      <div className="aprow-hd">
        <Avatar name={a.agentName} kind={r ? 'good' : 'neutral'} />
        <span className="nm">{a.agentName}</span>
        <span className="id">{`${a.stats.evalCount} değerlendirme`}</span>
        {a.stats.evalCount > 0 ? <ScorePill score={a.stats.avgScore} /> : null}
        {a.stats.criticalCount > 0 ? <Pill tone="bad">{`${a.stats.criticalCount} kritik`}</Pill> : null}
        {status}
        {r ? <span className="date">{`${r.authorName} · ${fullDateTime(r.createdAt)}`}</span> : null}
      </div>

      {r && r.body !== null && !open ? <div className="aprow-txt">{r.body}</div> : null}

      {open ? (
        <div style={{ paddingLeft: 38, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="inp" style={{ minHeight: 120 }}
            value={draft} onChange={(ev) => onDraft(ev.target.value)}
            placeholder="Temsilcinin bu haftaki genel performansı, güçlü yönleri ve gelişim alanları…"
          />
          {error ? <Note error>{error}</Note> : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn pri sm" type="button" disabled={busy || !draft.trim()} onClick={onSave}>
              <Icon name="check" /><span>{busy ? 'Kaydediliyor…' : 'Gönder'}</span>
            </button>
            <button className="btn ghost sm" type="button" disabled={busy} onClick={onCancel}>
              <span>Vazgeç</span>
            </button>
          </div>
        </div>
      ) : null}

      {action ? (
        <div className="aprow-act">
          <button className="btn soft sm" type="button" onClick={onEdit}>
            <Icon name="edit_note" /><span>{action}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The qms reminder. Shown on every screen but this one while a report of the DUE week is
 * missing. It re-fetches on each navigation (`routeKey`), so a report sent from the screen
 * clears it on the way out.
 */
export function WeeklyReportBanner({ routeKey }: { routeKey: string }) {
  const [board, setBoard] = useState<WeeklyReportBoard | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.getWeeklyReports()
      .then((b) => { if (!cancelled) setBoard(b); })
      .catch(() => { /* a reminder is not worth an error box */ });
    return () => { cancelled = true; };
  }, [routeKey]);

  if (!board) return null;
  const total = board.brands.reduce((s, b) => s + b.total, 0);
  const submitted = board.brands.reduce((s, b) => s + b.submitted, 0);
  if (total === 0 || submitted >= total) return null;

  return (
    <div
      role="link" tabIndex={0} style={{ cursor: 'pointer', marginBottom: 16 }}
      onClick={() => navigate('weekly')}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate('weekly'); }}
    >
      <Callout tone={board.overdue ? 'bad' : 'warn'} icon="speaker_notes">
        <b>{board.overdue ? 'Haftalık raporların son teslimi geçti' : 'Haftalık rapor hatırlatması'}</b>
        <span>{` · ${board.week.label} · `}</span>
        <span>{`${submitted}/${total} gönderildi`}</span>
        <span>{` · son teslim ${fullDateTime(board.deadline)}`}</span>
      </Callout>
    </div>
  );
}
